import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { fetchLeadLandingData, submitLeadLanding } from "../utils/api";
import { UIUtils } from "../utils/uiUtils";
import { Button } from "../components/Button";
import Loader from "../components/Loader";
import { usePostLoginProcessor } from "../utils/PostLoginProcessor";
import { useBottomNavBar } from "../contexts/BottomNavBarContext";
import { CategorySelectionStep } from "../components/CategorySelectionStep";
import { HeroGoalOptions } from "../components/HeroGoalOptions";
import {
  CATEGORY_OPTION_MAP,
  CategoryOption,
  OTHER_OPTION_LABEL,
  OTHER_GOAL_PREFIX,
} from "../utils/categoryOptions";

interface LeadLandingData {
  eligibility: boolean;
  first_name: string;
  is_verified: boolean;
  email: string;
  access_token: string;
}

type CategoryKey = keyof typeof CATEGORY_OPTION_MAP;

type HeroGoalOption = {
  label: string;
  icon: string;
  goal?: string;
  categoryKey?: CategoryKey;
};

const HERO_GOAL_OPTIONS: HeroGoalOption[] = [
  {
    label: "Fix or replace something at home",
    icon: "/images/pony/spanner.svg",
    categoryKey: "fix",
  },
  {
    label: "Help with bills",
    icon: "/images/pony/document.svg",
    goal: "I need help covering my household bills right now.",
  },
  {
    label: "Explore credit options",
    icon: "/images/pony/money.svg",
    goal: "I'm exploring credit options to manage an upcoming expense.",
  },
  {
    label: "Change in circumstances",
    icon: "/images/pony/tick.svg",
    goal: "My circumstances have changed and I need guidance on what to do next.",
  },
  {
    label: "Check benefits",
    icon: "/images/pony/pie.svg",
    goal: "I want to check if I'm missing out on any benefits.",
  },
  {
    label: "Manage debt or money worries",
    icon: "/images/pony/graph.svg",
    goal: "I need help managing debt or money worries.",
  },
];

const LeadLanding: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState<LeadLandingData | null>(null);
  const [loanPurpose, setLoanPurpose] = useState("");
  const navigate = useNavigate();
  const [goalError, setGoalError] = useState("");
  const [emailError, setEmailError] = useState("");
  const { processPostLogin } = usePostLoginProcessor();
  const [email, setEmail] = useState("");
  const [currentStep, setCurrentStep] = useState<"hero" | "category">("hero");
  const [heroCustomGoal, setHeroCustomGoal] = useState("");
  const [selectedHeroLabel, setSelectedHeroLabel] = useState("");
  const [categoryKey, setCategoryKey] = useState<CategoryKey | null>(null);
  const [selectedCategoryOption, setSelectedCategoryOption] = useState("");
  const [otherCategoryText, setOtherCategoryText] = useState("");
  const rtj = searchParams.get("rtj");
  const { setIsVisible } = useBottomNavBar();
  const categoryContent = categoryKey
    ? CATEGORY_OPTION_MAP[categoryKey]
    : undefined;
  useEffect(() => {
    setIsVisible(false);
    return () => setIsVisible(true);
  }, [setIsVisible]);

  useEffect(() => {
    const fetchData = async () => {
      if (!rtj) {
        UIUtils.showMessageOnSnackBar("Invalid URL");
        setLoading(false);
        return;
      }

      try {
        const response = await fetchLeadLandingData(rtj);
        setData(response.data);
        setEmail(response.data.email);
        // Redirect if not eligible
        if (!response.data.eligibility) {
          UIUtils.trackNREvents(
            "lead_landing_ineligible",
            "Link expired, redirecting to get-started"
          );
          navigate("/");
          return;
        }
      } catch (err) {
        UIUtils.showMessageOnSnackBar(
          "Something went wrong. Please try again later."
        );
        UIUtils.trackException("Lead landing fetch error", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [rtj, navigate]);

  useEffect(() => {
    setSelectedCategoryOption("");
    setOtherCategoryText("");
  }, [categoryKey]);

  const sanitizeGoal = (text: string): string => {
    const div = document.createElement("div");
    div.textContent = text;
    const sanitized = div.textContent || "";
    const MAX_GOAL_LENGTH = 250;
    return sanitized.slice(0, MAX_GOAL_LENGTH);
  };

  const buildOtherGoal = (text: string): string => {
    const trimmedText = text.trim();
    if (!trimmedText) {
      return "";
    }
    return sanitizeGoal(`${OTHER_GOAL_PREFIX}${trimmedText}`);
  };

  const validateForm = (goalOverride?: string): boolean => {
    let errors = 0;
    const resolvedGoal = goalOverride ?? loanPurpose;
    if (!resolvedGoal) {
      setGoalError("Please tell us why you are looking for a loan");
      errors += 1;
    } else {
      setGoalError("");
    }
    if (!data?.is_verified) {
      if (!email) {
        setEmailError("Please enter a valid email");
        UIUtils.showMessageOnSnackBar("Please enter a valid email");
        errors += 1;
      } else {
        setEmailError("");
      }
    } else {
      setEmailError("");
    }
    return errors === 0;
  };

  const submitLeadLandingForm = async (goalOverride?: string) => {
    if (!data) {
      return;
    }
    const intentValue = goalOverride ?? loanPurpose;
    const sanitizedIntent = sanitizeGoal(intentValue);
    if (!validateForm(sanitizedIntent)) {
      UIUtils.trackFormSubmit("0", "error");
      return;
    }
    if (sanitizedIntent !== loanPurpose) {
      setLoanPurpose(sanitizedIntent);
    }
    UIUtils.trackFormSubmit("0", "success");
    setSubmitting(true);
    try {
      const request = data.is_verified
        ? { intent: sanitizedIntent }
        : {
            intent: sanitizedIntent,
            confirmed_email: email,
          };
      const response = await submitLeadLanding(data.access_token, request);
      const { status } = response.data;

      UIUtils.trackNREvents("lead_landing_submit", status);

      if (status === "VERIFIED") {
        UIUtils.trackNREvents(
          "lead_landing_vsuccess",
          "Redirecting verified user to afforda app page"
        );
        const accessToken = response.data.access_token;
        api.setToken(accessToken);
        const userData = await api.fetchUserData();

        const { redirectTo } = processPostLogin(
          userData.data,
          accessToken,
          "lead_id"
        );

        navigate(redirectTo, { replace: true });
      } else if (status === "PENDING_VERIFICATION") {
        UIUtils.trackNREvents(
          "lead_landing_success",
          "Redirecting user to email verification"
        );
        const refid = response.data.reference_id;
        navigate(`/email-verification/${refid}`);
      } else if (status === "EXPIRED") {
        UIUtils.trackNREvents("lead_landing_existing_user", "Link expired");
        UIUtils.showMessageOnSnackBar(
          "It looks like this link has expired. If you're an existing user, please log in to continue. If you're new to Afforda, please register."
        );
      } else {
        UIUtils.trackNREvents(
          "lead_landing_error",
          "Error in lead landing submit"
        );
        UIUtils.showMessageOnSnackBar(
          "Something went wrong. Please try again after some time."
        );
      }
    } catch (err) {
      UIUtils.trackException("Lead landing submit error", err);
      UIUtils.showMessageOnSnackBar(
        "Something went wrong. Please try again after sometime."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleResolvedGoal = async (goalValue: string) => {
    const sanitizedValue = sanitizeGoal(goalValue);
    setLoanPurpose(sanitizedValue);
    setGoalError("");
    await submitLeadLandingForm(sanitizedValue);
  };

  const handleHeroGoalChange = (value: string) => {
    setHeroCustomGoal(value);
    if (goalError) {
      setGoalError("");
    }
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (emailError) {
      setEmailError("");
    }
  };

  const handleHeroSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedGoal = heroCustomGoal.trim();
    if (!trimmedGoal) {
      setGoalError("Please tell us what you need help with");
      return;
    }
    await handleResolvedGoal(trimmedGoal);
    setHeroCustomGoal("");
  };

  const handleHeroOptionSelect = (option: HeroGoalOption) => {
    setSelectedHeroLabel(option.label);
    if (goalError) {
      setGoalError("");
    }
    if (option.categoryKey && CATEGORY_OPTION_MAP[option.categoryKey]) {
      setCategoryKey(option.categoryKey);
      setCurrentStep("category");
      return;
    }
    if (option.goal) {
      void handleResolvedGoal(option.goal);
    }
  };

  const handleBackToHero = () => {
    if (submitting) {
      return;
    }
    setCurrentStep("hero");
    setCategoryKey(null);
    setSelectedHeroLabel("");
    setSelectedCategoryOption("");
    setOtherCategoryText("");
    setGoalError("");
    setHeroCustomGoal("");
  };

  const handleOtherCategoryInput = (value: string) => {
    setOtherCategoryText(value);
    if (goalError) {
      setGoalError("");
    }
  };

  const handleCategorySelection = (option: CategoryOption) => {
    setSelectedCategoryOption(option.label);
    if (option.label !== OTHER_OPTION_LABEL) {
      setOtherCategoryText("");
    }
  };

  const handleCategoryContinue = async () => {
    if (!categoryContent) {
      return;
    }
    if (!selectedCategoryOption) {
      UIUtils.showMessageOnSnackBar("Please pick an option to continue");
      return;
    }
    const selectedOption = categoryContent.options.find(
      (option) => option.label === selectedCategoryOption
    );
    if (!selectedOption) {
      UIUtils.showMessageOnSnackBar("Please pick an option to continue");
      return;
    }
    if (
      selectedOption.label === OTHER_OPTION_LABEL &&
      !otherCategoryText.trim()
    ) {
      setGoalError("Please tell us what needs fixing");
      return;
    }
    const finalGoal =
      selectedOption.label === OTHER_OPTION_LABEL
        ? buildOtherGoal(otherCategoryText)
        : selectedOption.goal;
    await handleResolvedGoal(finalGoal);
  };


  if (loading) {
    return <Loader message="Loading..." />;
  }

  if (!data) return null;

  if (!data.eligibility) {
    return <Loader message="Redirecting..." />;
  }
  const renderHeroSection = () => (
    <section className="hero-section text-center pb-12 md:pb-20">
      <div className="container-custom">
        <h1 className="hero-title mb-12">
          It's great to see you here {data.first_name}
        </h1>
        <p className="mb-4" style={{ maxWidth: '780px', marginLeft: 'auto', marginRight: 'auto' }}>We understand you were looking for a loan. If you can tell us below about why you needed a loan, we can look for ways we may be able to help.</p>
        <HeroGoalOptions
          options={HERO_GOAL_OPTIONS}
          selectedLabel={selectedHeroLabel}
          submitting={submitting}
          onSelect={handleHeroOptionSelect}
        />

        <div className="hero-browse-card">
          <form className="hero-browse-form" onSubmit={handleHeroSubmit}>
            <label className="sr-only" htmlFor="hero-browse-input">
              Enter what you need help with
            </label>
            <div className="hero-browse-input-wrap">
              <textarea
                id="hero-browse-input"
                className="hero-browse-input"
                placeholder="Type here for something else"
                name="category"
                rows={2}
                required
                value={heroCustomGoal}
                onChange={(e) => handleHeroGoalChange(e.target.value)}
                disabled={submitting}
              />
            </div>
            {currentStep === "hero" && goalError && (
              <p className="mt-2 text-sm text-red">{goalError}</p>
            )}
            {!data?.is_verified && (
              <>
                <p className="mt-4 mb-2 text-base text-gray-800 text-left">
                  We’ll be sending you a code to verify your email address, so
                  please confirm that it is correct.
                </p>
                <div className="mb-6 text-left">
                  <label htmlFor="email" className="form-label">
                    Email
                  </label>
                  <input
                    data-sensitive="true"
                    id="email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    className="form-input w-full rounded-2xl border border-black/10 bg-white/90 text-base placeholder:text-gray-500 focus:border-black focus:ring-0"
                    placeholder="Email"
                    aria-label="Email"
                  />
                  {emailError && (
                    <p className="text-red text-sm mt-1">{emailError}</p>
                  )}
                </div>
              </>
            )}
            <div className="hero-browse-footer">
              <p className="hero-browse-description">
                We ask because understanding your world helps us shape a better Afforda experience for
                you.
              </p>
              <Button
                type="submit"
                onClick={() => {}}
                className="btn btn-primary"
                disabled={submitting}
                isLoading={submitting}
                loadingText="Checking..."
                text="Get started"
                variant="primary"
              />
            </div>
          </form>
        </div>
        <p className="hero-reassurance">Afforda is 100% free to use</p>
      </div>
    </section>
  );

  if (currentStep === "category") {
    return (
      <div className="landing-static-page py-10">
        <div className="content-container">
          <CategorySelectionStep
            categoryContent={categoryContent}
            selectedCategoryOption={selectedCategoryOption}
            otherCategoryText={otherCategoryText}
            goalError={goalError}
            submitting={submitting}
            onBack={handleBackToHero}
            onCategorySelect={handleCategorySelection}
            onOtherInput={handleOtherCategoryInput}
            onContinue={handleCategoryContinue}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="landing-static-page">
      {renderHeroSection()}
    </div>
  );
};

export default LeadLanding;
